// Google Ads metric catalogue + aggregation. PURE + tested.
//
// THE ONE RULE: every ratio is recomputed from SUMMED numerator / SUMMED
// denominator. Nothing here ever averages a ratio, and no ratio is ever stored on
// the wire — `GoogleAdsTotals` carries only additive quantities precisely so that
// a ratio cannot be summed by accident downstream.
//
// MONEY IS PER-CURRENCY and never converted or blended. A money RATIO (avg CPC,
// CPA, value/conversion) is computed inside each currency — dividing an AUD
// numerator by a denominator summed across AUD+NZD would be silently wrong.
//
// ⚠️ IMPRESSION SHARE RETURNS null WHERE IT WAS NOT MEASURED, and null must reach
// the UI as "—". Google writes 0.0 rather than NULL when a share does not apply
// (measured: 170 campaign-days report 0% click share while carrying 1,719 clicks),
// so the view layer nulls those and this module keeps them null by refusing to
// divide when the weight is 0. Defaulting them to 0 would tell a client they won
// none of the auction when in truth we could not measure it.
//
// TWO DENOMINATORS, deliberately not interchangeable:
//   - `searchEligibleImpressions` — the exact denominator for search IS and both
//     lost-IS components. Licensed by Google's documented identity (IS +
//     lost-to-budget + lost-to-rank = 1), which the live data confirms both
//     row-wise (1.0000-1.0964, mean 1.0056) and after aggregation
//     (20.30 + 29.13 + 51.86).
//   - `*ShareWeight` — impression weights for top / absolute-top / exact-match /
//     click share, whose real denominators the Transfer does not expose. Those
//     are impression-weighted averages: the standard approach, materially better
//     than a flat mean, and NOT exact. Named apart so the two can't be crossed.

import type {
  CurrencyAmount,
  GoogleAdsMetricKey,
  GoogleAdsMetricKind,
  GoogleAdsMetrics,
  GoogleAdsTotals,
} from '../types/googleAdsReporting.js';

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export function emptyTotals(): GoogleAdsTotals {
  return {
    cost: 0,
    impressions: 0,
    clicks: 0,
    interactions: 0,
    conversions: 0,
    conversionsValue: 0,
    viewThroughConversions: 0,
    searchImpressions: 0,
    searchEligibleImpressions: 0,
    searchBudgetLostImpressions: 0,
    searchRankLostImpressions: 0,
    searchTopWeighted: 0,
    searchAbsoluteTopWeighted: 0,
    searchExactMatchWeighted: 0,
    searchClickShareWeighted: 0,
    contentWeighted: 0,
    searchShareWeight: 0,
    searchTopShareWeight: 0,
    searchAbsoluteTopShareWeight: 0,
    searchExactMatchShareWeight: 0,
    searchClickShareWeight: 0,
    contentShareWeight: 0,
  };
}

const TOTAL_KEYS = Object.keys(emptyTotals()) as Array<keyof GoogleAdsTotals>;

/** Add `b` into `a` in place. Every field is additive by construction. */
export function addTotals(a: GoogleAdsTotals, b: GoogleAdsTotals): GoogleAdsTotals {
  for (const k of TOTAL_KEYS) a[k] += b[k];
  return a;
}

export function sumTotals(list: readonly GoogleAdsTotals[]): GoogleAdsTotals {
  return list.reduce<GoogleAdsTotals>((acc, t) => addTotals(acc, t), emptyTotals());
}

// ---------------------------------------------------------------------------
// Ratio helpers
// ---------------------------------------------------------------------------

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** A ratio, or 0 when the denominator is 0. For metrics where 0 is meaningful. */
const ratio = (num: number, den: number): number => (den > 0 ? num / den : 0);

/**
 * A ratio, or NULL when the denominator is 0 — for metrics where a zero
 * denominator means UNMEASURED rather than zero. This is the function that keeps
 * "we couldn't measure your impression share" from rendering as "you got 0%".
 */
const ratioOrNull = (num: number, den: number): number | null =>
  den > 0 ? num / den : null;

/**
 * As a percentage, or null when unmeasured.
 *
 * ⚠️ EXPORTED so the client portal computes its competitive-position figures
 * through the SAME rule the staff dashboard does, rather than restating "a zero
 * denominator means unmeasured" in a second place. A client and their account
 * manager disagreeing about an impression share is the credibility problem the
 * whole portal is built to avoid.
 */
export const sharePctOrNull = (num: number, den: number): number | null => {
  const r = ratioOrNull(num, den);
  return r === null ? null : r2(r * 100);
};

const pctOrNull = sharePctOrNull;

// ---------------------------------------------------------------------------
// Per-currency aggregation
// ---------------------------------------------------------------------------

/** Totals split by native currency — the unit every money metric is computed in. */
export type TotalsByCurrency = Map<string, GoogleAdsTotals>;

export function addToCurrency(
  byCurrency: TotalsByCurrency,
  currency: string,
  totals: GoogleAdsTotals,
): void {
  const existing = byCurrency.get(currency);
  if (existing) addTotals(existing, totals);
  else byCurrency.set(currency, addTotals(emptyTotals(), totals));
}

/** Sorted currency list, so display order is stable across renders. */
export function currenciesOf(byCurrency: TotalsByCurrency): string[] {
  return [...byCurrency.keys()].sort();
}

/** Collapse per-currency totals into one — valid ONLY for non-money metrics. */
export function flattenTotals(byCurrency: TotalsByCurrency): GoogleAdsTotals {
  return sumTotals([...byCurrency.values()]);
}

/** Build a per-currency money array from a value computed inside each currency. */
function perCurrency(
  byCurrency: TotalsByCurrency,
  compute: (t: GoogleAdsTotals) => number,
): CurrencyAmount[] {
  return currenciesOf(byCurrency).map((currency) => ({
    currency,
    amount: r2(compute(byCurrency.get(currency)!)),
  }));
}

/**
 * Compute the full metric block from per-currency totals.
 *
 * Money + ROAS are computed WITHIN each currency; counts and percentages are
 * computed from the flattened totals (they are currency-agnostic, and a CTR is
 * the same question whichever currency paid for it).
 */
export function computeMetrics(byCurrency: TotalsByCurrency): GoogleAdsMetrics {
  const flat = flattenTotals(byCurrency);

  return {
    // --- money, per-currency -------------------------------------------------
    cost: perCurrency(byCurrency, (t) => t.cost),
    avgCpc: perCurrency(byCurrency, (t) => ratio(t.cost, t.clicks)),
    // CPM = cost per THOUSAND impressions.
    avgCpm: perCurrency(byCurrency, (t) => ratio(t.cost, t.impressions) * 1000),
    costPerConversion: perCurrency(byCurrency, (t) => ratio(t.cost, t.conversions)),
    conversionsValue: perCurrency(byCurrency, (t) => t.conversionsValue),
    valuePerConversion: perCurrency(byCurrency, (t) => ratio(t.conversionsValue, t.conversions)),
    // ROAS is a 'multiple' — value / cost inside ONE currency. Blending an AUD
    // numerator with an NZD denominator would be meaningless, hence per-currency.
    roas: perCurrency(byCurrency, (t) => ratio(t.conversionsValue, t.cost)),

    // --- counts --------------------------------------------------------------
    impressions: flat.impressions,
    clicks: flat.clicks,
    interactions: flat.interactions,
    // Google attribution is fractional; kept fractional, rounded at display.
    conversions: r2(flat.conversions),

    // --- percentages ---------------------------------------------------------
    ctr: r2(ratio(flat.clicks, flat.impressions) * 100),
    interactionRate: r2(ratio(flat.interactions, flat.impressions) * 100),
    // Google's "conversion rate" is conversions / INTERACTIONS, not / clicks —
    // interactions is the broader denominator that makes the metric comparable
    // across campaign types (a video view is an interaction, not a click).
    conversionRate: r2(ratio(flat.conversions, flat.interactions) * 100),

    // --- impression share: exact, eligible-impression denominator ------------
    searchImpressionShare: pctOrNull(flat.searchImpressions, flat.searchEligibleImpressions),
    searchBudgetLostImpressionShare: pctOrNull(
      flat.searchBudgetLostImpressions,
      flat.searchEligibleImpressions,
    ),
    searchRankLostImpressionShare: pctOrNull(
      flat.searchRankLostImpressions,
      flat.searchEligibleImpressions,
    ),

    // --- impression share: impression-weighted (different denominator) -------
    searchTopImpressionShare: pctOrNull(flat.searchTopWeighted, flat.searchTopShareWeight),
    searchAbsoluteTopImpressionShare: pctOrNull(
      flat.searchAbsoluteTopWeighted,
      flat.searchAbsoluteTopShareWeight,
    ),
    searchClickShare: pctOrNull(flat.searchClickShareWeighted, flat.searchClickShareWeight),
    contentImpressionShare: pctOrNull(flat.contentWeighted, flat.contentShareWeight),
  };
}

/** Convenience for a single-currency scope (a tree row is one currency). */
export function computeMetricsForCurrency(
  currency: string,
  totals: GoogleAdsTotals,
): GoogleAdsMetrics {
  const map: TotalsByCurrency = new Map([[currency, totals]]);
  return computeMetrics(map);
}

// ---------------------------------------------------------------------------
// The selectable metric catalogue
// ---------------------------------------------------------------------------

export interface GoogleAdsMetricDef {
  key: GoogleAdsMetricKey;
  label: string;
  kind: GoogleAdsMetricKind;
  /** Short help shown on the metric picker + KPI tile tooltip. */
  hint?: string;
  /**
   * True when this metric can legitimately be absent (impression share family).
   * Drives the "—" render; a non-nullable metric showing 0 really is 0.
   */
  nullable?: boolean;
  /** Lower is better (cost-style metrics) — used for delta colouring. */
  lowerIsBetter?: boolean;
}

/** Catalogue order = the order the picker and the default columns present. */
export const GOOGLE_ADS_METRICS: GoogleAdsMetricDef[] = [
  { key: 'cost', label: 'Cost', kind: 'money' },
  { key: 'impressions', label: 'Impressions', kind: 'count' },
  { key: 'clicks', label: 'Clicks', kind: 'count' },
  { key: 'ctr', label: 'CTR', kind: 'pct', hint: 'Clicks ÷ impressions' },
  { key: 'avgCpc', label: 'Avg. CPC', kind: 'money', lowerIsBetter: true },
  { key: 'avgCpm', label: 'Avg. CPM', kind: 'money', lowerIsBetter: true },
  {
    key: 'interactions',
    label: 'Interactions',
    kind: 'count',
    hint: 'Clicks, video views and other engagements — the cross-campaign-type equivalent of clicks',
  },
  { key: 'interactionRate', label: 'Interaction rate', kind: 'pct' },
  {
    key: 'conversions',
    label: 'Conversions',
    kind: 'decimal',
    hint: 'Fractional — Google attributes conversions across ads',
  },
  {
    key: 'conversionRate',
    label: 'Conv. rate',
    kind: 'pct',
    hint: 'Conversions ÷ interactions (Google’s definition)',
  },
  { key: 'costPerConversion', label: 'Cost / conv.', kind: 'money', lowerIsBetter: true },
  { key: 'conversionsValue', label: 'Conv. value', kind: 'money' },
  { key: 'valuePerConversion', label: 'Value / conv.', kind: 'money' },
  { key: 'roas', label: 'ROAS', kind: 'multiple', hint: 'Conversion value ÷ cost' },
  {
    key: 'searchImpressionShare',
    label: 'Search IS',
    kind: 'pct',
    nullable: true,
    hint: 'Impressions won ÷ impressions eligible on the Search network',
  },
  { key: 'searchTopImpressionShare', label: 'Top IS', kind: 'pct', nullable: true },
  {
    key: 'searchAbsoluteTopImpressionShare',
    label: 'Abs. top IS',
    kind: 'pct',
    nullable: true,
  },
  {
    key: 'searchBudgetLostImpressionShare',
    label: 'Lost IS (budget)',
    kind: 'pct',
    nullable: true,
    lowerIsBetter: true,
    hint: 'Share of eligible impressions lost because budget ran out',
  },
  {
    key: 'searchRankLostImpressionShare',
    label: 'Lost IS (rank)',
    kind: 'pct',
    nullable: true,
    lowerIsBetter: true,
    hint: 'Share of eligible impressions lost to Ad Rank (bid + quality)',
  },
  { key: 'searchClickShare', label: 'Click share', kind: 'pct', nullable: true },
  { key: 'contentImpressionShare', label: 'Content IS', kind: 'pct', nullable: true },
];

export const GOOGLE_ADS_METRIC_ORDER: GoogleAdsMetricKey[] = GOOGLE_ADS_METRICS.map((m) => m.key);

const METRIC_BY_KEY = new Map<GoogleAdsMetricKey, GoogleAdsMetricDef>(
  GOOGLE_ADS_METRICS.map((m) => [m.key, m]),
);

export function metricDef(key: GoogleAdsMetricKey): GoogleAdsMetricDef | undefined {
  return METRIC_BY_KEY.get(key);
}

/** The KPI tiles + table columns a fresh dashboard opens with. */
export const GOOGLE_ADS_DEFAULT_KPIS: GoogleAdsMetricKey[] = [
  'cost',
  'impressions',
  'clicks',
  'ctr',
  'conversions',
  'costPerConversion',
  'searchImpressionShare',
  'searchBudgetLostImpressionShare',
];

export const GOOGLE_ADS_DEFAULT_COLUMNS: GoogleAdsMetricKey[] = [
  'cost',
  'impressions',
  'clicks',
  'ctr',
  'avgCpc',
  'conversions',
  'costPerConversion',
  'searchImpressionShare',
];

export const GOOGLE_ADS_DEFAULT_TREND: GoogleAdsMetricKey[] = ['cost', 'clicks', 'conversions'];

/**
 * Is this one of THIS dashboard's metrics? The predicate the widget decoder
 * takes, so a stored widget naming a Meta metric is dropped rather than rendered
 * as something else.
 */
export function isGoogleAdsMetricKey(key: string): boolean {
  return METRIC_BY_KEY.has(key as GoogleAdsMetricKey);
}

/** Drop unknown keys, dedupe, restore catalogue order; empty → the fallback set. */
export function sanitizeMetricKeys(
  stored: readonly string[],
  fallback: readonly GoogleAdsMetricKey[],
): GoogleAdsMetricKey[] {
  const known = new Set(GOOGLE_ADS_METRIC_ORDER);
  const picked = new Set<GoogleAdsMetricKey>();
  for (const k of stored) {
    if (known.has(k as GoogleAdsMetricKey)) picked.add(k as GoogleAdsMetricKey);
  }
  const ordered = GOOGLE_ADS_METRIC_ORDER.filter((k) => picked.has(k));
  return ordered.length > 0 ? ordered : [...fallback];
}

// ---------------------------------------------------------------------------
// Reading a metric back out (render / sort / CSV)
// ---------------------------------------------------------------------------

const MONEY_KEYS = new Set<GoogleAdsMetricKey>([
  'cost',
  'avgCpc',
  'avgCpm',
  'costPerConversion',
  'conversionsValue',
  'valuePerConversion',
  'roas',
]);

/** Per-currency amounts for a money / multiple metric ([] for other kinds). */
export function metricMoney(m: GoogleAdsMetrics, key: GoogleAdsMetricKey): CurrencyAmount[] {
  switch (key) {
    case 'cost':
      return m.cost;
    case 'avgCpc':
      return m.avgCpc;
    case 'avgCpm':
      return m.avgCpm;
    case 'costPerConversion':
      return m.costPerConversion;
    case 'conversionsValue':
      return m.conversionsValue;
    case 'valuePerConversion':
      return m.valuePerConversion;
    case 'roas':
      return m.roas;
    default:
      return [];
  }
}

/**
 * The scalar value of a non-money metric. `null` means NOT MEASURED and must
 * render "—" — only the impression-share family can return it.
 */
export function metricScalar(m: GoogleAdsMetrics, key: GoogleAdsMetricKey): number | null {
  switch (key) {
    case 'impressions':
      return m.impressions;
    case 'clicks':
      return m.clicks;
    case 'interactions':
      return m.interactions;
    case 'conversions':
      return m.conversions;
    case 'ctr':
      return m.ctr;
    case 'interactionRate':
      return m.interactionRate;
    case 'conversionRate':
      return m.conversionRate;
    case 'searchImpressionShare':
      return m.searchImpressionShare;
    case 'searchTopImpressionShare':
      return m.searchTopImpressionShare;
    case 'searchAbsoluteTopImpressionShare':
      return m.searchAbsoluteTopImpressionShare;
    case 'searchBudgetLostImpressionShare':
      return m.searchBudgetLostImpressionShare;
    case 'searchRankLostImpressionShare':
      return m.searchRankLostImpressionShare;
    case 'searchClickShare':
      return m.searchClickShare;
    case 'contentImpressionShare':
      return m.contentImpressionShare;
    default:
      return null;
  }
}

export function isMoneyMetric(key: GoogleAdsMetricKey): boolean {
  return MONEY_KEYS.has(key);
}

/**
 * One comparable number for sorting a table by a metric. Money sums its
 * per-currency amounts as a sort proxy (a row is single-currency in practice);
 * an unmeasured metric sorts null so it lands last rather than as a fake 0.
 */
export function metricSortValue(m: GoogleAdsMetrics, key: GoogleAdsMetricKey): number | null {
  if (isMoneyMetric(key)) {
    const amounts = metricMoney(m, key);
    return amounts.length > 0 ? amounts.reduce((n, a) => n + a.amount, 0) : null;
  }
  return metricScalar(m, key);
}
